import { useSyncExternalStore } from 'react';

type LogLevel = 'log' | 'info' | 'warn' | 'error' | 'debug';

export type LogEntry = {
  level: LogLevel;
  ts: number;
  text: string;
};

const MAX = 300;
const KEY = 'bedtimer-log';

function stringifyArgs(args: unknown[]): string {
  return args
    .map((a) => {
      if (typeof a === 'string') return a;
      if (a instanceof Error) {
        return `${a.name}: ${a.message}${a.stack ? '\n' + a.stack : ''}`;
      }
      try {
        return JSON.stringify(
          a,
          (_, v) => (v instanceof Error ? `${v.name}: ${v.message}` : v),
          2
        );
      } catch {
        return String(a);
      }
    })
    .join(' ');
}

let buffer: LogEntry[] = [];
const listeners = new Set<() => void>();

function load() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return;
    const arr = JSON.parse(raw);
    if (Array.isArray(arr)) buffer = arr.slice(-MAX);
  } catch {
    /* ignore */
  }
}

let saveTimer: ReturnType<typeof setTimeout> | undefined;
function scheduleSave() {
  if (saveTimer !== undefined) return;
  saveTimer = setTimeout(() => {
    saveTimer = undefined;
    try {
      localStorage.setItem(KEY, JSON.stringify(buffer));
    } catch {
      /* quota or private mode — drop silently */
    }
  }, 200);
}

function push(level: LogLevel, args: unknown[]) {
  const entry: LogEntry = {
    level,
    ts: Date.now(),
    text: stringifyArgs(args),
  };
  buffer =
    buffer.length >= MAX
      ? [...buffer.slice(-(MAX - 1)), entry]
      : [...buffer, entry];
  scheduleSave();
  for (const l of listeners) l();
}

function getSnapshot(): LogEntry[] {
  return buffer;
}

export function clearLog() {
  buffer = [];
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
  for (const l of listeners) l();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

export function useLog(): LogEntry[] {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

let installed = false;

export function installLogCapture() {
  if (installed) return;
  installed = true;
  load();

  const levels: LogLevel[] = ['log', 'info', 'warn', 'error', 'debug'];
  for (const level of levels) {
    const orig = console[level].bind(console);
    console[level] = (...args: unknown[]) => {
      try {
        push(level, args);
      } catch {
        /* never let logging itself throw */
      }
      orig(...args);
    };
  }

  window.addEventListener('error', (ev) => {
    const where = ev.filename ? ` at ${ev.filename}:${ev.lineno}:${ev.colno}` : '';
    push('error', [`window.error: ${ev.message}${where}`, ev.error]);
  });

  window.addEventListener('unhandledrejection', (ev) => {
    push('error', ['unhandled rejection:', ev.reason]);
  });
}

// Auto-install on import so it runs before any other module-level code.
installLogCapture();
